// Idioma do painel do organizador e das páginas públicas do evento (pt/en/es).
//
// **Sem biblioteca de i18n, de propósito.** O SPA já tem build e `tsc -b` como
// gate bloqueante no CI (`web-unit-tests`), e é o compilador que faz o
// trabalho que uma lib faria em runtime: cada área declara seus textos com
// `defineMessages`, o português define a FORMA, e inglês e espanhol que
// esquecem uma chave (ou trazem uma a mais) não compilam. Lib de chave em
// string (`t('wizard.step1.title')`) erraria isso só na tela, em produção.
//
// **Trocar de idioma recarrega a página.** Texto montado fora de componente
// (rótulo de coluna, CSV, status calculado em `lib/`) leria o idioma antigo
// até o próximo render, e caçar cada `useMemo` que guarda texto é o tipo de
// bug que só aparece na tela de quem trocou. O recarregamento custa um
// segundo numa ação que se faz uma vez; o seletor fica longe do wizard pra
// não jogar fora formulário não salvo.
//
// Ordem de resolução, a mesma da landing (#522): `?lang=` (e grava) →
// `localStorage` → idiomas do navegador → pt. A chave é `wm_lang`, igual à da
// landing; as origens são diferentes, então não há compartilhamento, só
// coerência pra quem depura.
import type { ReactElement, ReactNode } from 'react';

export const LANGS = ['pt', 'en', 'es'] as const;
export type Lang = (typeof LANGS)[number];

/// Nome de cada idioma no PRÓPRIO idioma, nunca traduzido: quem não lê
/// português precisa achar "English" no seletor sem saber o que é "Inglês".
export const LANG_LABELS: Readonly<Record<Lang, string>> = {
  pt: 'Português',
  en: 'English',
  es: 'Español',
};

export const LANG_STORAGE_KEY = 'wm_lang';

export function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && (LANGS as readonly string[]).includes(value);
}

/// Regra PURA da escolha de idioma (testada em `i18n.test.ts`).
///
/// `navigator` é a lista de preferência do navegador, na ordem dele: um
/// alemão com inglês como segunda opção recebe inglês, não português. Só o
/// prefixo de duas letras conta, então `gl-ES` (galego) e `ca` (catalão) caem
/// no português e não no espanhol: errar o idioma de alguém é pior que
/// mostrar o default do produto.
export function resolveLang(input: {
  query: string | null;
  stored: string | null;
  navigator: readonly string[];
}): Lang {
  if (isLang(input.query)) return input.query;
  if (isLang(input.stored)) return input.stored;
  for (const tag of input.navigator) {
    const prefix = tag.slice(0, 2).toLowerCase();
    if (isLang(prefix)) return prefix;
  }
  return 'pt';
}

let current: Lang | null = null;

function navigatorLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  if (navigator.languages && navigator.languages.length > 0) return navigator.languages;
  return navigator.language ? [navigator.language] : [];
}

/// `localStorage` lança em Safari privado e com cookie bloqueado: idioma
/// nunca derruba a página, no máximo deixa de ser lembrado.
function readStored(): string | null {
  try {
    return window.localStorage.getItem(LANG_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(lang: Lang): void {
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // Sem armazenamento, o idioma vale até fechar a aba. Não é erro.
  }
}

function detect(): Lang {
  // Teste (vitest em `node`) e qualquer execução sem DOM: português, que é o
  // que as asserções existentes das regras puras esperam.
  if (typeof window === 'undefined') return 'pt';
  let query: string | null = null;
  try {
    query = new URLSearchParams(window.location.search).get('lang');
  } catch {
    query = null;
  }
  const lang = resolveLang({ query, stored: readStored(), navigator: navigatorLanguages() });
  // Link compartilhado com `?lang=en` vira preferência: a próxima página do
  // mesmo site, sem o parâmetro, continua em inglês.
  if (isLang(query)) writeStored(query);
  return lang;
}

/// Idioma corrente. Resolve na primeira chamada e fica fixo até a página
/// recarregar, inclusive pra texto montado no carregamento do módulo.
export function getLang(): Lang {
  if (current == null) current = detect();
  return current;
}

/// Troca o idioma e recarrega (ver o cabeçalho do arquivo). Com `?lang=` na
/// URL, o parâmetro é reescrito em vez de removido: o link que a pessoa
/// copiar dali em diante abre no idioma que ela escolheu.
export function setLang(lang: Lang): void {
  writeStored(lang);
  if (typeof window === 'undefined') {
    current = lang;
    return;
  }
  const url = new URL(window.location.href);
  if (url.searchParams.has('lang')) {
    url.searchParams.set('lang', lang);
    window.location.replace(url.toString());
  } else {
    window.location.reload();
  }
}

/// Só pra teste: fixa o idioma sem DOM nem recarga.
export function __setLangForTests(lang: Lang | null): void {
  current = lang;
}

const DEFAULT_LOCALE: Readonly<Record<Lang, string>> = {
  pt: 'pt-BR',
  en: 'en-US',
  es: 'es-ES',
};

/// Tag BCP 47 pra `Intl`/`toLocale*`. O português é sempre `pt-BR` (é o
/// produto). Em inglês e espanhol vale a variante do navegador quando ela é
/// do mesmo idioma: um britânico vê `08/10/2026`, um americano `10/8/2026`,
/// e um mexicano o formato dele. Sem variante, o default da tabela.
export function intlLocale(lang: Lang = getLang()): string {
  if (lang === 'pt') return DEFAULT_LOCALE.pt;
  const variant = navigatorLanguages().find((t) => t.toLowerCase().startsWith(`${lang}-`));
  return variant ?? DEFAULT_LOCALE[lang];
}

/// Aplica o idioma ao documento: `<html lang>` (leitor de tela, hifenização,
/// tradutor do navegador) e o título da aba. Chamado uma vez no boot.
export function applyLangToDocument(title: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = getLang() === 'pt' ? 'pt-BR' : getLang();
  document.title = title;
}

// ─── mensagens ───────────────────────────────────────────────────────

/// A forma que o inglês e o espanhol precisam cumprir, derivada do português:
/// texto vira `string`, função mantém os parâmetros (plural, interpolação),
/// JSX vira `ReactNode` (frase com negrito no meio), e objeto aninhado agrupa.
export type MessageShape<T> = T extends string
  ? string
  : T extends (...args: infer A) => infer R
    ? (...args: A) => MessageShape<R>
    : T extends ReactElement
      ? ReactNode
      : T extends readonly (infer U)[]
        ? readonly MessageShape<U>[]
        : T extends object
          ? { readonly [K in keyof T]: MessageShape<T[K]> }
          : T;

export interface MessageBundle<M> {
  /// Mensagens no idioma corrente.
  (): M;
  readonly namespace: string;
  /// Os três idiomas, pra trava de paridade e pra quem precisa de um idioma
  /// específico (ex.: e-mail de suporte sempre em português).
  readonly all: Readonly<Record<Lang, M>>;
}

const registry = new Map<string, MessageBundle<unknown>>();

/// Registro de todas as áreas carregadas, pra trava de paridade.
export function registeredBundles(): ReadonlyMap<string, MessageBundle<unknown>> {
  return registry;
}

/// Declara os textos de uma área nos três idiomas.
///
/// ```ts
/// export const signupT = defineMessages('signup', {
///   pt: { title: 'Inscrição', athletes: (n: number) => (n === 1 ? '1 atleta' : `${n} atletas`) },
///   en: { title: 'Registration', athletes: (n) => (n === 1 ? '1 athlete' : `${n} athletes`) },
///   es: { title: 'Inscripción', athletes: (n) => (n === 1 ? '1 atleta' : `${n} atletas`) },
/// });
/// // no componente ou na regra pura:
/// const t = signupT();
/// t.title; t.athletes(3);
/// ```
///
/// `NoInfer` deixa o português sozinho definir a forma; sem ele, uma chave a
/// mais no inglês entraria no tipo em vez de reprovar.
export function defineMessages<T extends object>(
  namespace: string,
  all: { pt: T; en: NoInfer<MessageShape<T>>; es: NoInfer<MessageShape<T>> },
): MessageBundle<MessageShape<T>> {
  const table = all as unknown as Record<Lang, MessageShape<T>>;
  const bundle = Object.assign(() => table[getLang()], { namespace, all: table });
  if (registry.has(namespace)) {
    // Duas áreas com o mesmo nome escondem uma da trava de paridade. Em dev
    // o HMR recarrega o módulo e re-registra o mesmo nome; por isso é aviso
    // e não exceção.
    console.warn(`[i18n] namespace duplicado: ${namespace}`);
  }
  registry.set(namespace, bundle as MessageBundle<unknown>);
  return bundle;
}
